import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Phone, Mail, Building, Calendar, DollarSign,
  FileText, CheckCircle, Clock, MessageSquare, Send, Trash2,
  Edit, Save, X, Plus, ChevronDown, Copy, ExternalLink,
  Upload, Download, Brain, Loader2, Sparkles, FileUp, FolderOpen,
  SendHorizonal, Landmark
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import CRMLayout from '../../components/CRM/CRMLayout';
import { EmailComposer, LenderSubmissionModal } from '../../components/CRM/EmailComposer';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STAGES = [
  { id: 'lead', label: 'New Lead', color: 'bg-blue-500' },
  { id: 'contacted', label: 'Contacted', color: 'bg-yellow-500' },
  { id: 'application', label: 'Application', color: 'bg-orange-500' },
  { id: 'submitted', label: 'Submitted', color: 'bg-purple-500' },
  { id: 'approved', label: 'Approved', color: 'bg-green-500' },
  { id: 'declined', label: 'Declined', color: 'bg-red-500' },
  { id: 'docs_out', label: 'Docs Out', color: 'bg-teal-500' },
  { id: 'settled', label: 'Settled', color: 'bg-emerald-500' },
  { id: 'lost', label: 'Lost', color: 'bg-gray-500' }
];

export default function DealDetail() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [newNote, setNewNote] = useState('');
  const [newTask, setNewTask] = useState({ title: '', due_date: '', priority: 'medium' });
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState({});
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [uploading, setUploading] = useState({});
  const [aiLoading, setAiLoading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('other');
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [showLenderSubmission, setShowLenderSubmission] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState(null);
  const fileInputRefs = useRef({});

  useEffect(() => {
    fetchDeal();
    fetchEmailTemplates();
  }, [dealId]);

  const fetchDeal = async () => {
    try {
      const response = await axios.get(`${API}/crm/deals/${dealId}`);
      setDeal(response.data);
      setEditData(response.data);
    } catch (error) {
      console.error('Error fetching deal:', error);
      toast.error('Failed to load deal');
      navigate('/crm/pipeline');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await axios.get(`${API}/crm/email-templates`);
      setEmailTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleStageChange = async (newStage) => {
    try {
      await axios.put(`${API}/crm/deals/${dealId}`, { stage: newStage });
      setDeal(prev => ({ ...prev, stage: newStage }));
      toast.success(`Stage updated to ${STAGES.find(s => s.id === newStage)?.label}`);
      fetchDeal(); // Refresh to get new activity
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/crm/deals/${dealId}`, editData);
      setDeal(editData);
      setEditing(false);
      toast.success('Deal updated successfully');
    } catch (error) {
      toast.error('Failed to save changes');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await axios.post(`${API}/crm/activities`, {
        deal_id: dealId,
        activity_type: 'note',
        description: newNote
      });
      setNewNote('');
      fetchDeal();
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title.trim()) return;
    try {
      await axios.post(`${API}/crm/tasks`, {
        deal_id: dealId,
        ...newTask,
        due_date: newTask.due_date ? new Date(newTask.due_date).toISOString() : null
      });
      setNewTask({ title: '', due_date: '', priority: 'medium' });
      setShowTaskForm(false);
      fetchDeal();
      toast.success('Task created');
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleToggleTask = async (taskId, isCompleted) => {
    try {
      await axios.put(`${API}/crm/tasks/${taskId}?is_completed=${!isCompleted}`);
      fetchDeal();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDocumentToggle = async (docId, isReceived) => {
    try {
      await axios.put(`${API}/crm/documents/${docId}?is_received=${!isReceived}`);
      fetchDeal();
    } catch (error) {
      toast.error('Failed to update document');
    }
  };

  const handleFileUpload = async (file, docId, category) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File exceeds 10MB limit');
      return;
    }
    setUploading(prev => ({ ...prev, [docId || 'new']: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('deal_id', dealId);
      formData.append('category', category || 'other');
      if (docId) formData.append('doc_id', docId);
      await axios.post(`${API}/crm/documents/upload`, formData);
      toast.success(`${file.name} uploaded`);
      fetchDeal();
      setShowUploadForm(false);
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(prev => ({ ...prev, [docId || 'new']: false }));
    }
  };

  const handleDownload = async (docId, filename) => {
    try {
      const response = await axios.get(`${API}/crm/documents/${docId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'download';
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const handleAINotes = async (action) => {
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/crm/ai-notes`, {
        deal_id: dealId,
        action,
        notes_text: newNote || undefined
      });
      toast.success(action === 'analyze' ? 'AI analysis generated' : 'Submission summary generated');
      fetchDeal();
      setNewNote('');
    } catch (error) {
      toast.error('AI analysis failed');
    } finally {
      setAiLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this deal?')) return;
    try {
      await axios.delete(`${API}/crm/deals/${dealId}`);
      toast.success('Deal deleted');
      navigate('/crm/pipeline');
    } catch (error) {
      toast.error('Failed to delete deal');
    }
  };

  const copyEmail = (template) => {
    const populated = populateTemplate(template);
    navigator.clipboard.writeText(`Subject: ${populated.subject}\n\n${populated.body}`);
    toast.success('Email copied to clipboard');
  };

  const populateTemplate = (template) => {
    if (!template) return { subject: '', body: '' };
    let subject = template.subject;
    let body = template.body;
    
    const replacements = {
      '{client_name}': deal?.client_name || '',
      '{business_name}': deal?.business_name || '',
      '{finance_type}': deal?.finance_type || '',
      '{amount}': deal?.amount || 0,
      '{term_months}': deal?.term_months || 60,
      '{asset_description}': deal?.asset_description || '',
      '{lender_submitted}': deal?.lender_submitted || '[Lender Name]',
      '{approved_amount}': deal?.approved_amount || deal?.amount || 0,
      '{approved_rate}': deal?.approved_rate || '[Rate]'
    };
    
    Object.entries(replacements).forEach(([key, value]) => {
      const regex = new RegExp(key.replace(/[{}]/g, '\\$&'), 'g');
      subject = subject.replace(regex, typeof value === 'number' ? value.toLocaleString('en-AU') : value);
      body = body.replace(regex, typeof value === 'number' ? value.toLocaleString('en-AU') : value);
    });
    
    // Handle format specifiers like {amount:,.0f}
    body = body.replace(/\$\{([^}]+):,[^}]+\}/g, (match, key) => {
      const value = replacements[`{${key}}`];
      return typeof value === 'number' ? `$${value.toLocaleString('en-AU')}` : match;
    });
    
    return { subject, body };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!deal) return null;

  const currentStage = STAGES.find(s => s.id === deal.stage);

  return (
    <CRMLayout>
    <div className="min-h-screen bg-navy-900 pb-20">
      {/* Header */}
      <div className="bg-navy-800 border-b border-white/5">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/crm/pipeline" className="text-gray-400 hover:text-white" data-testid="back-to-pipeline">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-heading text-white">{deal.client_name}</h1>
                <p className="text-gray-400">{deal.business_name} • ${deal.amount?.toLocaleString('en-AU')} {deal.finance_type}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {editing ? (
                <>
                  <Button variant="outline" onClick={() => setEditing(false)}>
                    <X className="h-4 w-4 mr-2" /> Cancel
                  </Button>
                  <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">
                    <Save className="h-4 w-4 mr-2" /> Save
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => { setEmailTemplate({ to: deal.client_email }); setShowEmailComposer(true); }}
                    className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                    data-testid="email-client-btn"
                  >
                    <Mail className="h-4 w-4 mr-2" /> Email Client
                  </Button>
                  <Button 
                    onClick={() => setShowLenderSubmission(true)}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                    data-testid="submit-lender-btn"
                  >
                    <Landmark className="h-4 w-4 mr-2" /> Submit to Lender
                  </Button>
                  <Button variant="outline" onClick={() => setEditing(true)} className="border-gray-600">
                    <Edit className="h-4 w-4 mr-2" /> Edit
                  </Button>
                  <Button variant="outline" onClick={handleDelete} className="border-red-500/50 text-red-400 hover:bg-red-500/10">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stage Progress */}
      <div className="bg-navy-800/50 border-b border-white/5">
        <div className="px-6 py-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {STAGES.map((stage, idx) => (
              <button
                key={stage.id}
                onClick={() => handleStageChange(stage.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-all ${
                  deal.stage === stage.id 
                    ? `${stage.color} text-white` 
                    : 'bg-navy-900 text-gray-400 hover:bg-navy-800'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${stage.color}`}></span>
                {stage.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* AI Recommendation */}
            {deal.ai_recommendation && (
              <div className="bg-gradient-to-r from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-xl p-6">
                <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                  <span className="text-purple-400">AI</span> Deal Recommendation
                </h3>
                <p className="text-gray-300 text-sm">{deal.ai_recommendation}</p>
                {deal.ai_matched_lenders && deal.ai_matched_lenders.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {deal.ai_matched_lenders.map((lender, idx) => (
                      <div key={idx} className="bg-navy-900/50 rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <span className="text-white font-medium">{lender.name}</span>
                          <p className="text-gray-400 text-xs">{lender.reason}</p>
                        </div>
                        <span className="text-purple-400 font-mono">{lender.estimated_rate}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Client & Finance Details */}
            <div className="bg-navy-800 rounded-xl border border-white/5 p-6">
              <h3 className="text-white font-semibold mb-4">Deal Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <DetailRow label="Client Name" value={deal.client_name} editing={editing} field="client_name" editData={editData} setEditData={setEditData} />
                <DetailRow label="Business Name" value={deal.business_name} editing={editing} field="business_name" editData={editData} setEditData={setEditData} />
                <DetailRow label="ABN" value={deal.abn} editing={editing} field="abn" editData={editData} setEditData={setEditData} />
                <DetailRow label="Industry" value={deal.industry} editing={editing} field="industry" editData={editData} setEditData={setEditData} />
                <DetailRow label="Years Trading" value={deal.years_trading} editing={editing} field="years_trading" editData={editData} setEditData={setEditData} />
                <DetailRow label="GST Registered" value={deal.gst_registered ? 'Yes' : 'No'} />
                <DetailRow label="Finance Type" value={deal.finance_type} editing={editing} field="finance_type" editData={editData} setEditData={setEditData} />
                <DetailRow label="Amount" value={`$${deal.amount?.toLocaleString('en-AU')}`} editing={editing} field="amount" editData={editData} setEditData={setEditData} type="number" />
                <DetailRow label="Term" value={`${deal.term_months} months`} editing={editing} field="term_months" editData={editData} setEditData={setEditData} type="number" />
                <DetailRow label="Balloon" value={`${deal.balloon_percent}%`} editing={editing} field="balloon_percent" editData={editData} setEditData={setEditData} type="number" />
                <DetailRow label="Asset" value={deal.asset_description} editing={editing} field="asset_description" editData={editData} setEditData={setEditData} className="col-span-2" />
              </div>
              
              {/* Lender Details */}
              {(deal.stage === 'submitted' || deal.stage === 'approved' || deal.stage === 'docs_out' || deal.stage === 'settled') && (
                <div className="mt-6 pt-6 border-t border-white/5">
                  <h4 className="text-white font-medium mb-4">Lender Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <DetailRow label="Lender" value={deal.lender_submitted} editing={editing} field="lender_submitted" editData={editData} setEditData={setEditData} />
                    <DetailRow label="Submitted Rate" value={deal.submitted_rate ? `${deal.submitted_rate}%` : '-'} editing={editing} field="submitted_rate" editData={editData} setEditData={setEditData} type="number" />
                    <DetailRow label="Approved Rate" value={deal.approved_rate ? `${deal.approved_rate}%` : '-'} editing={editing} field="approved_rate" editData={editData} setEditData={setEditData} type="number" />
                    <DetailRow label="Approved Amount" value={deal.approved_amount ? `$${deal.approved_amount?.toLocaleString('en-AU')}` : '-'} editing={editing} field="approved_amount" editData={editData} setEditData={setEditData} type="number" />
                    <DetailRow label="Est. Commission" value={deal.estimated_commission ? `$${deal.estimated_commission?.toLocaleString('en-AU')}` : '-'} editing={editing} field="estimated_commission" editData={editData} setEditData={setEditData} type="number" />
                  </div>
                </div>
              )}
            </div>

            {/* Activity & Notes */}
            <div className="bg-navy-800 rounded-xl border border-white/5 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">Activity & Notes</h3>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleAINotes('analyze')}
                    disabled={aiLoading}
                    size="sm"
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white text-xs"
                    data-testid="ai-analyze-btn"
                  >
                    {aiLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Brain className="h-3 w-3 mr-1" />}
                    AI Analysis
                  </Button>
                  <Button
                    onClick={() => handleAINotes('submission_summary')}
                    disabled={aiLoading}
                    size="sm"
                    className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs"
                    data-testid="ai-submission-btn"
                  >
                    {aiLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
                    Lender Summary
                  </Button>
                </div>
              </div>
              
              {/* Add Note */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note or context for AI..."
                  data-testid="note-input"
                  className="flex-1 px-4 py-2 bg-navy-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                />
                <Button onClick={handleAddNote} className="bg-purple-600 hover:bg-purple-700" data-testid="add-note-btn">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Activity List */}
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {deal.activities?.map((activity) => {
                  const isAI = activity.activity_type === 'ai_analysis' || activity.activity_type === 'ai_submission_summary';
                  return (
                    <div key={activity.id} className={`flex gap-3 p-3 rounded-lg ${isAI ? 'bg-purple-500/5 border border-purple-500/20' : 'bg-navy-900'}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isAI ? 'bg-purple-500/20 text-purple-400' :
                        activity.activity_type === 'stage_change' ? 'bg-yellow-500/20 text-yellow-400' :
                        activity.activity_type === 'task_completed' ? 'bg-green-500/20 text-green-400' :
                        activity.activity_type === 'document_uploaded' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {isAI ? <Brain className="h-4 w-4" /> : <MessageSquare className="h-4 w-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        {isAI && (
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-purple-400 text-xs font-semibold uppercase">
                              {activity.activity_type === 'ai_analysis' ? 'FlashIQ Analysis' : 'Lender Submission Summary'}
                            </span>
                            <button
                              onClick={() => { navigator.clipboard.writeText(activity.description); toast.success('Copied to clipboard'); }}
                              className="text-gray-500 hover:text-white"
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                        <div className={`text-sm ${isAI ? 'text-gray-200 whitespace-pre-wrap' : 'text-gray-300'}`}>
                          {activity.description}
                        </div>
                        <p className="text-gray-500 text-xs mt-1">
                          {activity.created_by === 'FlashIQ AI' ? 'FlashIQ AI' : ''} {new Date(activity.created_at).toLocaleString('en-AU')}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Info */}
            <div className="bg-navy-800 rounded-xl border border-white/5 p-6">
              <h3 className="text-white font-semibold mb-4">Contact</h3>
              <div className="space-y-3">
                {deal.client_phone && (
                  <a href={`tel:${deal.client_phone}`} className="flex items-center gap-3 text-gray-300 hover:text-purple-400">
                    <Phone className="h-4 w-4" />
                    {deal.client_phone}
                  </a>
                )}
                {deal.client_email && (
                  <a href={`mailto:${deal.client_email}`} className="flex items-center gap-3 text-gray-300 hover:text-purple-400">
                    <Mail className="h-4 w-4" />
                    {deal.client_email}
                  </a>
                )}
              </div>
              
              {/* Email Templates */}
              <div className="mt-4 pt-4 border-t border-white/5">
                <h4 className="text-gray-400 text-sm mb-2">Quick Email</h4>
                <div className="space-y-2">
                  {Object.entries(emailTemplates).map(([key, template]) => (
                    <button
                      key={key}
                      onClick={() => {
                        const populated = populateTemplate(template);
                        setEmailTemplate({ to: deal.client_email, subject: populated.subject, body: populated.body });
                        setShowEmailComposer(true);
                      }}
                      className="w-full text-left px-3 py-2 bg-navy-900 rounded text-sm text-gray-300 hover:text-white hover:bg-navy-900/80 flex items-center justify-between"
                    >
                      <span className="capitalize">{key.replace('_', ' ')}</span>
                      <Send className="h-3 w-3" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Tasks */}
            <div className="bg-navy-800 rounded-xl border border-white/5 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">Tasks</h3>
                <button onClick={() => setShowTaskForm(!showTaskForm)} className="text-purple-400 hover:text-purple-300">
                  <Plus className="h-5 w-5" />
                </button>
              </div>
              
              {showTaskForm && (
                <div className="mb-4 p-3 bg-navy-900 rounded-lg space-y-2">
                  <input
                    type="text"
                    value={newTask.title}
                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    placeholder="Task title"
                    className="w-full px-3 py-2 bg-navy-800 border border-gray-700 rounded text-white text-sm"
                  />
                  <input
                    type="datetime-local"
                    value={newTask.due_date}
                    onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                    className="w-full px-3 py-2 bg-navy-800 border border-gray-700 rounded text-white text-sm"
                  />
                  <div className="flex gap-2">
                    <Button onClick={handleAddTask} size="sm" className="bg-purple-600">Add</Button>
                    <Button onClick={() => setShowTaskForm(false)} size="sm" variant="outline">Cancel</Button>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                {deal.tasks?.map((task) => (
                  <div
                    key={task.id}
                    onClick={() => handleToggleTask(task.id, task.is_completed)}
                    className={`flex items-start gap-3 p-3 bg-navy-900 rounded-lg cursor-pointer ${task.is_completed ? 'opacity-50' : ''}`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      task.is_completed ? 'bg-green-500 border-green-500' : 'border-gray-600'
                    }`}>
                      {task.is_completed && <CheckCircle className="h-3 w-3 text-white" />}
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm ${task.is_completed ? 'text-gray-500 line-through' : 'text-white'}`}>
                        {task.title}
                      </p>
                      {task.due_date && (
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <Clock className="h-3 w-3" />
                          {new Date(task.due_date).toLocaleDateString('en-AU')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Document Checklist with Upload */}
            <div className="bg-navy-800 rounded-xl border border-white/5 p-6" data-testid="documents-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">Documents</h3>
                <button onClick={() => setShowUploadForm(!showUploadForm)} className="text-purple-400 hover:text-purple-300" data-testid="add-doc-btn">
                  <Plus className="h-5 w-5" />
                </button>
              </div>

              {/* Upload New Document */}
              {showUploadForm && (
                <div className="mb-4 p-3 bg-navy-900 rounded-lg space-y-2">
                  <select
                    value={uploadCategory}
                    onChange={(e) => setUploadCategory(e.target.value)}
                    className="w-full px-3 py-2 bg-navy-800 border border-gray-700 rounded text-white text-sm"
                    data-testid="upload-category-select"
                  >
                    <option value="privacy_consent">Privacy Consent Form</option>
                    <option value="drivers_licence">Driver's Licence</option>
                    <option value="bank_statements">Bank Statements</option>
                    <option value="financials">Financial Statements</option>
                    <option value="tax_returns">Tax Returns</option>
                    <option value="asset_quote">Asset Invoice/Quote</option>
                    <option value="contract">Contract</option>
                    <option value="other">Other</option>
                  </select>
                  <label className="flex items-center justify-center gap-2 px-4 py-3 bg-navy-800 border border-dashed border-gray-600 rounded-lg text-gray-400 hover:text-white hover:border-purple-500 cursor-pointer transition-colors">
                    <FileUp className="h-4 w-4" />
                    <span className="text-sm">Choose file (max 10MB)</span>
                    <input
                      type="file"
                      className="hidden"
                      data-testid="upload-file-input"
                      onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], null, uploadCategory)}
                    />
                  </label>
                  {uploading['new'] && <div className="flex items-center gap-2 text-purple-400 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</div>}
                </div>
              )}

              <div className="space-y-2">
                {deal.documents?.map((doc) => (
                  <div key={doc.id} className="p-3 bg-navy-900 rounded-lg">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleDocumentToggle(doc.id, doc.is_received)}
                        className="flex-shrink-0"
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          doc.is_received ? 'bg-green-500 border-green-500' : 'border-gray-600'
                        }`}>
                          {doc.is_received && <CheckCircle className="h-3 w-3 text-white" />}
                        </div>
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${doc.is_received ? 'text-gray-400' : 'text-white'}`}>
                          {doc.name}
                        </p>
                        {doc.original_filename && (
                          <p className="text-xs text-gray-500 truncate">{doc.original_filename}</p>
                        )}
                        {doc.is_received && doc.uploaded_at && (
                          <p className="text-xs text-gray-500">
                            Received {new Date(doc.uploaded_at).toLocaleDateString('en-AU')}
                            {doc.file_size ? ` · ${(doc.file_size / 1024).toFixed(0)}KB` : ''}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {doc.storage_path && (
                          <button
                            onClick={() => handleDownload(doc.id, doc.original_filename || doc.name)}
                            className="p-1.5 text-gray-500 hover:text-blue-400 rounded"
                            title="Download"
                            data-testid={`download-${doc.id}`}
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        )}
                        {!doc.storage_path && (
                          <>
                            <label className="p-1.5 text-gray-500 hover:text-purple-400 rounded cursor-pointer" title="Upload file">
                              <Upload className="h-4 w-4" />
                              <input
                                type="file"
                                className="hidden"
                                onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], doc.id, doc.category || doc.file_type)}
                              />
                            </label>
                            {uploading[doc.id] && <Loader2 className="h-4 w-4 animate-spin text-purple-400" />}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Email Composer Modal */}
    {showEmailComposer && (
      <EmailComposer
        deal={deal}
        onClose={() => { setShowEmailComposer(false); setEmailTemplate(null); }}
        onSent={fetchDeal}
        prefillTemplate={emailTemplate}
      />
    )}

    {/* Lender Submission Modal */}
    {showLenderSubmission && (
      <LenderSubmissionModal
        deal={deal}
        onClose={() => setShowLenderSubmission(false)}
        onSubmitted={fetchDeal}
      />
    )}
    </CRMLayout>
  );
}

function DetailRow({ label, value, editing, field, editData, setEditData, type = 'text', className = '' }) {
  if (editing && field) {
    return (
      <div className={className}>
        <label className="text-gray-500 text-xs">{label}</label>
        <input
          type={type}
          value={editData[field] || ''}
          onChange={(e) => setEditData({ ...editData, [field]: type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value })}
          className="w-full px-3 py-1.5 mt-1 bg-navy-900 border border-gray-700 rounded text-white text-sm"
        />
      </div>
    );
  }
  
  return (
    <div className={className}>
      <p className="text-gray-500 text-xs">{label}</p>
      <p className="text-white">{value || '-'}</p>
    </div>
  );
}
